import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map, switchMap, forkJoin, of } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ApolloStudentService {

    constructor(private apollo: Apollo) { }

    getStudentList(): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                student {
                    allStudent(pageNo:1) {
                        data {
                            id
                            firstName
                            lastName
                            gender
                            birthCertificateNo
                            myKidNo
                            createdDate
                            dateOfBirth
                            isOku
                            isActive
                            refOkuType {
                                id
                                name
                                description
                            }
                            class {
                                id
                                name
                                grade {
                                    id
                                    name
                                    isStaffSpecificStudent
                                }
                            }
                            masterStaffAssignee {
                                userRole {
                                    user {
                                        id
                                        firstName
                                        lastName
                                    }
                                }
                            }
                            schoolYear {
                                id
                                year
                                month
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                            }
                            user {
                                id
                                email
                                firstName
                                lastName
                                phoneNumber
                                userRoles {
                                    refRole {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.student?.allStudent?.data || [];
                return studentListData;
            })
        );
    }


    getStudentRegistrationList(): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                onlineStudentRegistration {
                    allOnlineStudentRegistration(pageNo:1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            firstName
                            lastName
                            gender
                            birthCertificateNo
                            myKidNo
                            dateOfBirth
                            isActive
                            isOku
                            status
                            refOkuType {
                                name
                                description
                            }
                            userId
                            grade {
                                id
                                name
                                isStaffSpecificStudent
                                capping
                                registrationStatus
                            }
                            class {
                                id
                                name
                                grade {
                                    name
                                    isStaffSpecificStudent
                                    capping
                                }
                            }
                            schoolYear {
                                year
                                month
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                            }
                            user {
                                id
                                email
                                firstName
                                lastName
                                phoneNumber
                                userRoles {
                                    refRole {
                                        name
                                    }
                                }
                            }
                            masterPayment {
                                id
                                year
                                fees {
                                    id
                                    name
                                    amount
                                    isMandatory
                                    discountType {
                                        name
                                        value
                                        type
                                    }
                                    otCalculation {
                                        name
                                        type
                                        calculationType
                                        isOverTime
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.onlineStudentRegistration?.allOnlineStudentRegistration?.data || [];
                return studentListData;
            })
        );
    }


    getStudentListByParentId(parentId: number): Observable<any> {
        const GET_STUDENT = gql`
            query {
                student {
                    studentByParentId(parentId: ${parentId}) {
                        id
                        firstName
                        lastName
                        gender
                        birthCertificateNo
                        myKidNo
                        dateOfBirth
                        isOku
                        isActive
                        refOkuType {
                            id
                            name
                            description
                        }
                        class {
                            id
                            name
                            grade {
                                id
                                name
                                isStaffSpecificStudent
                                capping
                            }
                        }
                        schoolYear {
                            year
                            month
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                        }
                        user {
                            id
                            email
                            firstName
                            lastName
                            phoneNumber
                            userRoles {
                                id
                                refRole {
                                    id
                                    name
                                }
                            }
                        }
                        masterStaffAssignee {
                            id
                            userRole {
                                id
                                user {
                                    id
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                                refRole {
                                    id
                                    name
                                }
                            }
                        }
                        studentOperatingHour {
                            id
                            operatingHour {
                                id
                                name
                                startTime
                                endTime
                                bufferTimeForOTCalc
                                refOperatingType {
                                    name
                                }
                                fees {
                                    id
                                    name
                                    amount
                                    isMandatory
                                    discountType {
                                        name
                                        value
                                        type
                                    }
                                    otCalculation {
                                        name
                                        type
                                        calculationType
                                        isOverTime
                                    }
                                }
                            }
                        }
                        masterPayment {
                            id
                            year
                            fees {
                                id
                                name
                                amount
                                isMandatory
                                discountType {
                                    name
                                    value
                                    type
                                }
                                otCalculation {
                                    name
                                    type
                                    calculationType
                                    isOverTime
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_STUDENT,
            variables: { parentId: parentId }
        }).pipe(
            map(result => {
                const student = result?.data?.student?.studentByParentId;
                return student;
            })
        );
    }


    getCheckInOutList(startDate: string, endDate: string): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                studentCheckInCheckOut {
                    allStudentCheckInCheckOut(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            checkInDateTime
                            checkInRemarks
                            checkOutDateTime
                            checkOutRemarks
                            temperature
                            isSick
                            createdBy
                            refSickType {
                                id
                                name
                                description
                            }
                            studentCheckInCheckOutAttachment {
                                id
                                attachmentName
                                attachmentPath
                                attachmentType
                                attachmentBase64
                                isCheckIn
                                studentCheckInCheckOutId
                                studentCheckInCheckOutPhysicalRecordId
                            }
                            studentOperatingHour {
                                id
                                operatingHour {
                                    name
                                    startTime
                                    endTime
                                    refOperatingType {
                                        name
                                    }
                                    fees {
                                        name
                                        amount
                                        isMandatory
                                        discountType {
                                            name
                                            value
                                            type
                                        }
                                        otCalculation {
                                            name
                                            type
                                        }
                                    }
                                }
                            }
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    name
                                    grade {
                                        name
                                        isStaffSpecificStudent
                                    }
                                }
                                masterStaffAssignee {
                                    id
                                    userRole {
                                        id
                                        user {
                                            id
                                            firstName
                                            lastName
                                        }
                                    }
                                }
                            }
                            schoolYear {
                                year
                                month
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const studentListData = result?.data?.studentCheckInCheckOut?.allStudentCheckInCheckOut?.data || [];
                return studentListData;
            })
        );
    }

    private getCheckInOutListPage(startDate: string, endDate: string, pageNo: number): Observable<any> {
        const GET_STUDENTLIST = gql`
            query {
                studentCheckInCheckOut {
                    allStudentCheckInCheckOut(pageNo: ${pageNo}, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            checkInDateTime
                            checkInRemarks
                            checkOutDateTime
                            checkOutRemarks
                            temperature
                            isSick
                            createdBy
                            refSickType {
                                id
                                name
                                description
                            }
                            studentCheckInCheckOutAttachment {
                                id
                                attachmentName
                                attachmentPath
                                attachmentType
                                attachmentBase64
                                isCheckIn
                                studentCheckInCheckOutId
                                studentCheckInCheckOutPhysicalRecordId
                            }
                            studentOperatingHour {
                                id
                                operatingHour {
                                    name
                                    startTime
                                    endTime
                                    refOperatingType {
                                        name
                                    }
                                    fees {
                                        name
                                        amount
                                        isMandatory
                                        discountType {
                                            name
                                            value
                                            type
                                        }
                                        otCalculation {
                                            name
                                            type
                                        }
                                    }
                                }
                            }
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    name
                                    grade {
                                        name
                                        isStaffSpecificStudent
                                    }
                                }
                                masterStaffAssignee {
                                    id
                                    userRole {
                                        id
                                        user {
                                            id
                                            firstName
                                            lastName
                                        }
                                    }
                                }
                            }
                            schoolYear {
                                year
                                month
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const response = result?.data?.studentCheckInCheckOut?.allStudentCheckInCheckOut;
                return {
                    data: response?.data || [],
                    totalPages: response?.totalPages || 1,
                    pageNo: response?.pageNo || 1,
                    count: response?.count || 0
                };
            })
        );
    }

    getAllCheckInOutList(startDate: string, endDate: string): Observable<any[]> {
        return this.getCheckInOutListPage(startDate, endDate, 1).pipe(
            switchMap((firstPage: any) => {
                const totalPages = firstPage.totalPages || 1;
                if (totalPages <= 1) {
                    return of(firstPage.data || []);
                }
                
                // Fetch all remaining pages
                const pageObservables: Observable<any>[] = [of(firstPage.data || [])];
                for (let page = 2; page <= totalPages; page++) {
                    pageObservables.push(
                        this.getCheckInOutListPage(startDate, endDate, page).pipe(
                            map((pageResult: any) => pageResult.data || [])
                        )
                    );
                }
                
                return forkJoin(pageObservables).pipe(
                    map((pages: any[][]) => {
                        return pages.flat();
                    })
                );
            })
        );
    }

    getStudentNotCheckInList(startDate: string, endDate: string): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                studentCheckInCheckOut {
                    allStudentNotCheckedIn(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            birthCertificateNo
                            firstName
                            gender
                            id
                            lastName
                            myKidNo
                            userId
                            masterStaffAssignee {
                                userRole {
                                    user {
                                        id
                                        firstName
                                        lastName
                                    }
                                }
                            }
                            class {
                                id
                                name
                                grade {
                                    id
                                    name
                                    isStaffSpecificStudent
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.studentCheckInCheckOut?.allStudentNotCheckedIn?.data || [];
                return studentListData;
            })
        );
    }

    getStudentAttendanceSummary(startDate: string, endDate: string, staffUserRoleId?: number): Observable<{ present: number; absent: number }> {
        const staffFilter = staffUserRoleId != null ? staffUserRoleId : null;
        const GET_ATTENDANCE = gql`
            query {
                studentCheckInCheckOut {
                    allStudentCheckInCheckOut(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}", staffUserRoleId: ${staffFilter}) {
                        count
                    }
                    allStudentNotCheckedIn(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}", staffUserRoleId: ${staffFilter}) {
                        count
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_ATTENDANCE
        }).pipe(
            map(result => {
                const root = result?.data?.studentCheckInCheckOut;
                return {
                    present: Number(root?.allStudentCheckInCheckOut?.count ?? 0),
                    absent: Number(root?.allStudentNotCheckedIn?.count ?? 0)
                };
            })
        );
    }

    getStudentCount(): Observable<number> {
        const GET_COUNT = gql`
            query {
                student {
                    allStudent(pageNo: 1) {
                        count
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_COUNT
        }).pipe(
            map(result => Number(result?.data?.student?.allStudent?.count ?? 0))
        );
    }

    getStudentNotCheckInByStaffUserIdList(startDate: string, endDate: string, staffUserRoleId?: number | null): Observable<any[]> {
        let GET_STUDENTLIST = gql`
        query {
            studentCheckInCheckOut {
                allStudentNotCheckedIn(
                    pageNo: 1,
                    startDate: "${startDate}",
                    endDate: "${endDate}",
                    staffUserRoleId: ${staffUserRoleId}
                ) {
                    count
                    pageNo
                    pageSize
                    totalPages
                    data {
                        id
                        firstName
                        lastName
                        gender
                        birthCertificateNo
                        myKidNo
                        dateOfBirth
                        isActive
                        isOku
                        refOkuType {
                            name
                            description
                        }
                        class {
                            name
                            grade {
                                name
                                isStaffSpecificStudent
                            }
                        }
                    }
                }
            }
        }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.studentCheckInCheckOut?.allStudentNotCheckedIn?.data || [];
                return studentListData;
            })
        );
    }

    getOkuTypeList(): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                refOkuType {
                    allRefOkuTypes(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            description
                            isActive
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.refOkuType?.allRefOkuTypes?.data || [];
                return studentListData;
            })
        );
    }


    getSickTypeList(): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                refSickType {
                    allRefSickTypes(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            description
                            isActive
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.refSickType?.allRefSickTypes?.data || [];
                return studentListData;
            })
        );
    }


    createStudent(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_USER = gql`
            mutation {
                student {
                    addStudent(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.student?.addStudent;
                return user;
            })
        );
    }


    createStudentRegistration(input: any): Observable<any> {
        var studentList = "[";
        input.forEach((student: any) => {
            const inputString = Object.keys(student).map(key => {
                const value = student[key as keyof typeof student];
    
                if (value === null || value === undefined) {
                    return null;
                } else if (Array.isArray(value)) {
                    if (value.length === 0) {
                        return `${key}: null`;
                    } else {
                        return `${key}: [${value.join(', ')}]`;
                    }
                } else if (typeof value === 'string') {
                    const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                    return `${key}: "${escapedValue}"`;
                } else {
                    return `${key}: ${value}`;
                }
            }).filter(str => str !== null).join(', ');

            studentList = studentList + `{ ${inputString} }`
        });

        const CREATE_USER = gql`
            mutation {
                onlineStudentRegistration {
                    addOnlineStudentRegistration(input: { studentList: ${studentList} ] }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.onlineStudentRegistration?.addOnlineStudentRegistration;
                return user;
            })
        );
    }


    approveStudentRegistration(id: number): Observable<any> {
        const DELETE_STUDENT = gql`
            mutation {
                onlineStudentRegistration {
                    approveOnlineStudentRegistration(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_STUDENT,
        }).pipe(
            map(result => {
                const user = result?.data?.onlineStudentRegistration?.approveOnlineStudentRegistration;
                return user;
            })
        );
    }


    createCheckIn(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_USER = gql`
            mutation {
                studentCheckInCheckOut {
                    addStudentCheckIn(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.studentCheckInCheckOut?.addStudentCheckIn;
                return user;
            })
        );
    }


    createCheckOut(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_USER = gql`
            mutation {
                studentCheckInCheckOut {
                    addStudentCheckOut(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.studentCheckInCheckOut?.addStudentCheckOut;
                return user;
            })
        );
    }


    updateStudent(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_STUDENT = gql`
            mutation {
                student {
                    updateStudent(input:  { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_STUDENT
        }).pipe(
            map(result => {
                const user = result?.data?.student?.updateStudent;
                return user;
            })
        );
    }


    updateStudentRegistration(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_STUDENT = gql`
            mutation {
                onlineStudentRegistration {
                    updateOnlineStudentRegistration(input:  { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_STUDENT
        }).pipe(
            map(result => {
                const user = result?.data?.onlineStudentRegistration?.updateOnlineStudentRegistration;
                return user;
            })
        );
    }


    updateCheckIn(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_STUDENT = gql`
            mutation {
                studentCheckInCheckOut {
                    updateStudentCheckIn(input:  { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_STUDENT
        }).pipe(
            map(result => {
                const user = result?.data?.studentCheckInCheckOut?.updateStudentCheckIn;
                return user;
            })
        );
    }


    updateCheckOut(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_STUDENT = gql`
            mutation {
                studentCheckInCheckOut {
                    updateStudentCheckOut(input:  { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_STUDENT
        }).pipe(
            map(result => {
                const user = result?.data?.studentCheckInCheckOut?.updateStudentCheckOut;
                return user;
            })
        );
    }


    deleteStudent(id: number): Observable<any> {
        const DELETE_STUDENT = gql`
            mutation {
                student {
                    deleteStudent(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_STUDENT,
        }).pipe(
            map(result => {
                const user = result?.data?.student?.deleteStudent;
                return user;
            })
        );
    }


    deleteStudentRegistration(id: number): Observable<any> {
        const DELETE_STUDENT = gql`
            mutation {
                onlineStudentRegistration {
                    deleteOnlineStudentRegistration(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_STUDENT,
        }).pipe(
            map(result => {
                const user = result?.data?.onlineStudentRegistration?.deleteOnlineStudentRegistration;
                return user;
            })
        );
    }


    deleteCheckInOut(id: number): Observable<any> {
        const DELETE_STUDENT = gql`
            mutation {
                studentCheckInCheckOut {
                    deleteStudentCheckInCheckOut(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_STUDENT,
        }).pipe(
            map(result => {
                const user = result?.data?.studentCheckInCheckOut?.deleteStudentCheckInCheckOut;
                return user;
            })
        );
    }


    getStudentById(id: number): Observable<any> {
        const GET_STUDENT = gql`
            query {
                student {
                    studentById(id: ${id}) {
                        id
                        firstName
                        lastName
                        gender
                        birthCertificateNo
                        myKidNo
                        dateOfBirth
                        isOku
                        isActive
                        refOkuType {
                            id
                            name
                            description
                        }
                        grade {
                            id
                            name
                            isStaffSpecificStudent
                            capping
                            registrationStatus
                        }
                        class {
                            id
                            name
                            grade {
                                id
                                name
                                isStaffSpecificStudent
                            }
                        }
                        schoolYear {
                            id
                            year
                            month
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                        }
                        user {
                            id
                            email
                            firstName
                            lastName
                            phoneNumber
                            userRoles {
                                refRole {
                                    id
                                    name
                                }
                            }
                        }
                        masterStaffAssignee {
                            id
                            userRole {
                                id
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                                refRole {
                                    name
                                }
                            }
                        }
                        studentOperatingHour {
                            id
                            operatingHour {
                                id
                                name
                                startTime
                                endTime
                                bufferTimeForOTCalc
                                refOperatingType {
                                    name
                                }
                                fees {
                                    id
                                    name
                                    amount
                                    isMandatory
                                    discountType {
                                        name
                                        value
                                        type
                                    }
                                    otCalculation {
                                        name
                                        type
                                        calculationType
                                        isOverTime
                                    }
                                }
                            }
                        }
                        masterPayment {
                            id
                            year
                            fees {
                                id
                                name
                                amount
                                isMandatory
                                discountType {
                                    name
                                    value
                                    type
                                }
                                otCalculation {
                                    name
                                    type
                                    calculationType
                                    isOverTime
                                }
                                refFeesType {
                                    id
                                    name
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_STUDENT,
            variables: { id: id }
        }).pipe(
            map(result => {
                const student = result?.data?.student?.studentById[0];
                return student;
            })
        );
    }

    getStudentByParentId(parentId: number): Observable<any[]> {  // ✅ Return an array
        const GET_STUDENT = gql`
            query {
                student {
                    studentByParentId(parentId: ${parentId}) {
                        id
                        firstName
                        lastName
                        gender
                        birthCertificateNo
                        myKidNo
                        dateOfBirth
                        isOku
                        isActive
                        refOkuType {
                            id
                            name
                            description
                        }
                        class {
                            id
                            name
                            grade {
                                id
                                name
                                isStaffSpecificStudent
                            }
                        }
                        schoolYear {
                            id
                            year
                            month
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                        }
                        user {
                            id
                            email
                            firstName
                            lastName
                            phoneNumber
                            userRoles {
                                refRole {
                                    id
                                    name
                                }
                            }
                        }
                        masterStaffAssignee {
                            id
                            userRole {
                                id
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                                refRole {
                                    name
                                }
                            }
                        }
                        studentOperatingHour {
                            id
                            operatingHour {
                                id
                                name
                                startTime
                                endTime
                                bufferTimeForOTCalc
                                refOperatingType {
                                    name
                                }
                                fees {
                                    id
                                    name
                                    amount
                                    isMandatory
                                    discountType {
                                        name
                                        value
                                        type
                                    }
                                    otCalculation {
                                        name
                                        type
                                        calculationType
                                        isOverTime
                                    }
                                }
                            }
                        }
                        masterPayment {
                            id
                            year
                            fees {
                                id
                                name
                                amount
                                isMandatory
                                discountType {
                                    name
                                    value
                                    type
                                }
                                otCalculation {
                                    name
                                    type
                                    calculationType
                                    isOverTime
                                }
                                refFeesType {
                                    id
                                    name
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_STUDENT,
            variables: { parentId: parentId }
        }).pipe(
            map(result => {
                const students = result?.data?.student?.studentByParentId || []; // ✅ Return all students
                return students;
            })
        );
    }

    getStudentRegistrationById(id: number): Observable<any> {
        const GET_STUDENT = gql`
            query {
                onlineStudentRegistration {
                    onlineStudentRegistrationById(id: ${id}) {
                        id
                        firstName
                        lastName
                        gender
                        birthCertificateNo
                        myKidNo
                        dateOfBirth
                        isOku
                        isActive
                        status
                        refOkuType {
                            id
                            name
                            description
                        }
                        grade {
                            id
                            name
                            isStaffSpecificStudent
                            capping
                            registrationStatus
                        }
                        class {
                            id
                            name
                            grade {
                                id
                                name
                                isStaffSpecificStudent
                                capping
                            }
                        }
                        schoolYear {
                            id
                            year
                            month
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                        }
                        user {
                            id
                            email
                            firstName
                            lastName
                            phoneNumber
                            userRoles {
                                refRole {
                                    id
                                    name
                                }
                            }
                        }
                        masterPayment {
                            id
                            year
                            fees {
                                id
                                name
                                amount
                                isMandatory
                                discountType {
                                    name
                                    value
                                    type
                                }
                                otCalculation {
                                    name
                                    type
                                    calculationType
                                    isOverTime
                                }
                                refFeesType {
                                    id
                                    name
                                }
                            }
                        }
                        operatingHour {
                            id
                            name
                            startTime
                            endTime
                            bufferTimeForOTCalc
                            refOperatingType {
                                id
                                name
                            }
                        }
                        masterStaffAssignee {
                            id
                            refRole {
                                id
                                name
                            }
                            user {
                                id
                                email
                                firstName
                                lastName
                                phoneNumber
                                profilePictureBase64
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_STUDENT,
            variables: { id: id },
            fetchPolicy: 'network-only'
        }).pipe(
            map(result => {
                const student = result?.data?.onlineStudentRegistration?.onlineStudentRegistrationById[0];
                return student;
            })
        );
    }


    private removeTypename(data: any): any {
        if (Array.isArray(data)) {
            return data.map(item => this.removeTypename(item));
        } else if (data !== null && typeof data === 'object') {
            const newObj: any = {};
            Object.keys(data).forEach(key => {
                if (key !== '__typename') {
                    newObj[key] = this.removeTypename(data[key]);
                }
            });
            return newObj;
        }
        return data;
    }
}
